/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { TemplateType } from "@gnu-taler/taler-util";
import { preferences } from "../stores/preferences.js";
import type { VNode } from "preact";
import {
  useTemplates,
  useInventory,
  useCategories,
  useTemplateDetails,
  useMerchantConfig,
  usePayoutAccounts,
  useOtpDevices,
} from "../api/hooks.js";
import { CreateTemplateScreen } from "../screens/CreateTemplateScreen.js";

export function CreateTemplateRoute({ editId }: { editId?: string }): VNode {
  const { templates, createTemplate, updateTemplate } = useTemplates();
  const { template, resource: templateResource } = useTemplateDetails(editId);
  const {
    primaryCurrency,
    currencies,
    resource: configResource,
  } = useMerchantConfig();
  const { accounts } = usePayoutAccounts();
  const { devices, resource: devicesResource } = useOtpDevices();

  const enabled =
    preferences.value.advancedTools ||
    template?.templateType === TemplateType.INVENTORY_CART;
  const { products, resource: productsResource } = useInventory({ enabled });
  const { categories, resource: categoriesResource } = useCategories({
    enabled,
  });
  const payoutCurrencies = (accounts ?? [])
    .map((a: { currency?: string }) => a.currency)
    .filter((c: string | undefined): c is string => Boolean(c));

  return (
    <CreateTemplateScreen
      products={products}
      categories={categories}
      productsResource={productsResource}
      categoriesResource={categoriesResource}
      editId={editId}
      template={template}
      templateResource={templateResource}
      templates={templates}
      defaultCurrency={primaryCurrency}
      configuredCurrencies={Object.keys(currencies ?? {})}
      configResource={configResource}
      payoutCurrencies={payoutCurrencies}
      devices={devices}
      devicesResource={devicesResource}
      onCreateTemplate={(id, name, contract, extras, options) =>
        createTemplate(id, name, contract as never, extras as never, options)
      }
      onUpdateTemplate={(id, name, contract, extras) =>
        updateTemplate(id, name, contract as never, extras as never)
      }
    />
  );
}
